import { useState, useRef, useCallback } from 'react';

interface PhotoUploadSectionProps {
  photos: File[];
  onPhotosChange: (photos: File[]) => void;
  maxPhotos?: number;
  readonly?: boolean;
}

export function PhotoUploadSection({ 
  photos, 
  onPhotosChange, 
  maxPhotos = 3,
  readonly = false 
}: PhotoUploadSectionProps) {
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): boolean => {
    // 파일 타입 검증 (이미지만 허용)
    if (!file.type.startsWith('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return false;
    }

    // 서버 제한: 20MB 초과 방지
    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      alert('파일 크기는 20MB 이하만 업로드 가능합니다.');
      return false;
    }

    return true;
  }, []);

  const handleFileSelect = useCallback((newFiles: FileList | null) => {
    if (!newFiles || readonly) return;

    const validFiles: File[] = [];
    const remainingSlots = maxPhotos - photos.length;

    for (let i = 0; i < Math.min(newFiles.length, remainingSlots); i++) {
      const file = newFiles[i];
      if (validateFile(file)) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      onPhotosChange([...photos, ...validFiles]);
    }

    if (newFiles.length > remainingSlots) {
      alert(`최대 ${maxPhotos}장까지만 업로드 가능합니다.`);
    }

    // 파일 input 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [photos, onPhotosChange, maxPhotos, validateFile, readonly]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!readonly) {
      setDragOver(true);
    }
  }, [readonly]);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const removePhoto = useCallback((index: number) => {
    if (readonly) return;
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
  }, [photos, onPhotosChange, readonly]);

  const openFileDialog = useCallback(() => {
    if (!readonly && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [readonly]);

  const createImageUrl = useCallback((file: File) => {
    return URL.createObjectURL(file);
  }, []);

  return (
    <div className="w-full space-y-4">
      {/* Upload Area */}
      {photos.length < maxPhotos && !readonly && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={openFileDialog}
          className={`
            border-2 border-dashed rounded-xl p-6 text-center cursor-pointer
            transition-all duration-200
            ${dragOver 
              ? 'border-green-500 bg-green-50' 
              : 'border-gray-300 hover:border-gray-400'
            }
          `}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <svg 
                className="w-6 h-6 text-gray-400" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 6v6m0 0v6m0-6h6m-6 0H6" 
                />
              </svg>
            </div>
            <div>
              <p className="text-base font-medium text-gray-900">
                사진 추가하기
              </p>
              <p className="text-sm text-gray-500 mt-1">
                클릭하거나 드래그해서 업로드 (최대 {maxPhotos}장)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      {/* Photo Preview Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {photos.map((photo, index) => (
            <div key={index} className="relative aspect-square">
              <img
                src={createImageUrl(photo)}
                alt={`업로드된 사진 ${index + 1}`}
                className="w-full h-full object-cover rounded-lg border border-gray-200"
              />
              
              {/* Remove Button */}
              {!readonly && (
                <button
                  onClick={() => removePhoto(index)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full
                           flex items-center justify-center hover:bg-red-600 transition-colors
                           shadow-lg"
                >
                  <svg 
                    className="w-3 h-3" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M6 18L18 6M6 6l12 12" 
                    />
                  </svg>
                </button>
              )}

              {/* Photo Index */}
              <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white 
                            text-xs px-2 py-1 rounded">
                {index + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Photo Count Info */}
      {photos.length > 0 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            {photos.length}/{maxPhotos}장의 사진이 선택되었습니다
          </span>
          {photos.length === maxPhotos && (
            <span className="text-green-600 font-medium">
              ✓ 최대 업로드 완료
            </span>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="text-xs text-gray-500 space-y-1">
        <p>• 이미지 파일만 업로드 가능합니다 (JPG, PNG, GIF)</p>
        <p>• 파일 크기는 20MB 이하로 업로드해 주세요 (서버에서 자동 리사이징)</p>
        <p>• 최대 {maxPhotos}장까지 업로드 가능합니다</p>
      </div>
    </div>
  );
}
